import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
    getStandardWindowCodeEmbodiedCarbon,
    getStandardWindowCodeEmbodiedCarbonDisplay,
    getWindowLayersSnapshotForStandardEmbC,
    WINDOW_STANDARD_EMB_KEY,
} from "utils/enclosure/embodiedCarbon";
import {
    parseEmbodiedNum,
    useEmbodiedFieldId,
    useExpandableGradeCellHandlers,
} from "./embodiedFormUtils";

import GradeDisplay from "./GradeDisplay";
import PercentageDisplay from "./PercentageDisplay";
import { sanitizeSourceInput, USER_DEFINED_LABEL } from "./wallLayerEmbodiedCarbon";

import classes from "./style.module.scss";

/**
 * Whole-window embodied carbon (glazing + frame group), mirroring framing: grade strip + expandable EPD editor.
 * Custom values persist on `layers.overall` (emissions, storage, source, and a snapshot of the six layer fields).
 */
const EmbodiedCarbonStandardWindowCodeGrade = React.memo(
    ({
        layers,
        customPadding = "0 7px",
        customGap = "0.75rem",
        borderHeight = "100%",
        change,
        layersFieldPrefix,
        barMetric = "percentage",
    }) => {
        const [expanded, setExpanded] = useState(false);
        const [draftEmissions, setDraftEmissions] = useState("");
        const [draftStorage, setDraftStorage] = useState("");
        const [draftSource, setDraftSource] = useState("");

        const sourceInputId = useEmbodiedFieldId("volta-emb-window-overall-source");
        const expandCellHandlers = useExpandableGradeCellHandlers(setExpanded);

        const showEditor = typeof change === "function" && typeof layersFieldPrefix === "string";
        const embPath = `${layersFieldPrefix}.${WINDOW_STANDARD_EMB_KEY}`;
        const overallC = layers?.[WINDOW_STANDARD_EMB_KEY];

        const display = useMemo(() => getStandardWindowCodeEmbodiedCarbonDisplay({ layers }), [layers]);

        useEffect(() => {
            if (!expanded || !showEditor) return;
            if (overallC && overallC.emissions != null) {
                setDraftEmissions(String(overallC.emissions));
                setDraftStorage(overallC.storage != null ? String(overallC.storage) : "");
                setDraftSource(
                    sanitizeSourceInput(
                        overallC.source != null && overallC.source !== "" ? overallC.source : USER_DEFINED_LABEL
                    )
                );
                return;
            }
            const def = getStandardWindowCodeEmbodiedCarbon(layers);
            setDraftEmissions(def.emissions != null ? String(def.emissions) : "");
            setDraftStorage(def.storage != null ? String(def.storage) : "");
            setDraftSource("");
        }, [expanded, showEditor, overallC, layers]);

        const applyEmbC = useCallback(() => {
            if (!change) return;
            const e = parseEmbodiedNum(draftEmissions);
            if (e == null) return;
            const s = parseEmbodiedNum(draftStorage);
            const trimmed = sanitizeSourceInput(draftSource).trim();
            const nextSource = trimmed === "" ? USER_DEFINED_LABEL : trimmed;

            change(embPath, {
                emissions: e,
                storage: s ?? 0,
                source: nextSource,
                ...getWindowLayersSnapshotForStandardEmbC(layers),
            });
        }, [change, embPath, draftEmissions, draftStorage, draftSource, layers]);

        const resetEmbC = useCallback(() => {
            if (change) change(embPath, null);
            setExpanded(false);
        }, [change, embPath]);

        useEffect(() => {
            if (!showEditor) setExpanded(false);
        }, [showEditor]);

        const showGrade = barMetric === "grade";

        const gradeInner = (
            <GradeDisplay
                emissions={display.emissions}
                storage={display.storage}
                emissionGrade={display.emissionGrade}
                storageGrade={display.storageGrade}
                customPadding={customPadding}
                customGap={customGap}
                borderHeight={borderHeight}
                layout="framingGroup"
            />
        );

        const percentageInner = (
            <PercentageDisplay
                emissions={display.emissions}
                storage={display.storage}
                customPadding={customPadding}
                customGap={customGap}
                borderHeight={borderHeight}
                layout="framingGroup"
            />
        );

        if (!showEditor) {
            return <div className={classes.framingGroupGradeReadonly}>{gradeInner}</div>;
        }

        return (
            <div className={classes.framingGroupGradeRoot}>
                <div
                    className={`${classes.framingGroupGradeCell} ${display.hasMismatch ? classes.mismatch : ""} ${classes.clickable
                        }`}
                    {...expandCellHandlers}
                >
                    {showGrade ? gradeInner : percentageInner}
                </div>
                {expanded && (
                    <div
                        className={`${classes.editorPanel} ${classes.framingGroupPanel}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {display.hasMismatch && (
                            <div className={classes.mismatchNotice}>
                                Window selections changed since this EPD data was saved. Review or reset.
                            </div>
                        )}
                        <div className={classes.editorRow}>
                            <div className={classes.editorRowInputs}>
                                <label className={classes.miniLabel}>
                                    Emissions (kgCO₂/m²)
                                    <input
                                        className={classes.miniInput}
                                        type="text"
                                        inputMode="decimal"
                                        value={draftEmissions}
                                        onChange={(ev) => setDraftEmissions(ev.target.value)}
                                    />
                                </label>
                                <label className={classes.miniLabel}>
                                    Storage (kgCO₂/m²)
                                    <input
                                        className={classes.miniInput}
                                        type="text"
                                        inputMode="decimal"
                                        value={draftStorage}
                                        onChange={(ev) => setDraftStorage(ev.target.value)}
                                    />
                                </label>
                                <button
                                    type="button"
                                    className={classes.resetBtn}
                                    title="Reset to defaults"
                                    onClick={resetEmbC}
                                >
                                    <span className={classes.resetBtnIcon} aria-hidden="true">
                                        ↺
                                    </span>
                                </button>
                            </div>
                            <button type="button" className={classes.applyBtn} onClick={applyEmbC}>
                                Apply
                            </button>
                        </div>
                        <div className={classes.sourceRow}>
                            <label className={classes.sourceLabel} htmlFor={sourceInputId}>
                                Source
                            </label>
                            <input
                                className={`${classes.sourceInput} keeper-ignore`}
                                type="text"
                                name="voltaEmbWindowOverallCitation"
                                id={sourceInputId}
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="off"
                                spellCheck={false}
                                data-lpignore="true"
                                data-1p-ignore="true"
                                data-bwignore="true"
                                data-form-type="other"
                                placeholder={overallC ? USER_DEFINED_LABEL : "—"}
                                value={draftSource}
                                onChange={(ev) => setDraftSource(sanitizeSourceInput(ev.target.value))}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }
);

export default EmbodiedCarbonStandardWindowCodeGrade;
